import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { REGIONES, COMUNAS_CHILE } from "@/lib/constants";
import { BadgeCheck, MapPin, Star, Search, Scale, ArrowRight } from "lucide-react";

const ESPECIALIDADES_ABOG = [
  "Familia", "Civil", "Laboral", "Penal", "Comercial", "Tributario",
  "Inmobiliario", "Migratorio", "Contratos", "Sucesiones", "Consumidor",
  "Cobranza", "Constitucional", "Juzgado de Policía Local", "Otros",
];

export default function Lawyers() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    region: params.get("region") || "",
    comuna: params.get("comuna") || "",
    specialty: params.get("specialty") || "",
    q: params.get("q") || "",
  });

  const load = async () => {
    const q = Object.fromEntries(Object.entries(filters).filter(([_k, v]) => v));
    const { data } = await api.get("/lawyers", { params: q });
    setItems(data.items);
    setParams(q);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="container-app py-12" data-testid="lawyers-page">
      <div className="label-eyebrow">Directorio nacional</div>
      <h1 className="serif text-5xl mt-3">Abogados/as verificados</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">
        Abogados/as inscritos en la Corte Suprema de Chile, con perfil verificado por Colega Link.
        Filtra por región, comuna y especialidad.
      </p>

      <div className="mt-8 panel p-5 grid sm:grid-cols-5 gap-3">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input
            className="field pl-9"
            placeholder="Buscar por nombre o palabra clave"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="law-search"
          />
        </div>
        <select className="field" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })} data-testid="law-region">
          <option value="">Todas las regiones</option>
          {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="field" value={filters.comuna} onChange={(e) => setFilters({ ...filters, comuna: e.target.value })} data-testid="law-comuna">
          <option value="">Toda comuna</option>
          {COMUNAS_CHILE.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="field" value={filters.specialty} onChange={(e) => setFilters({ ...filters, specialty: e.target.value })} data-testid="law-specialty">
          <option value="">Toda especialidad</option>
          {ESPECIALIDADES_ABOG.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="btn-primary sm:col-span-5 sm:justify-self-start" data-testid="law-apply">Aplicar</button>
      </div>

      <ul className="mt-8 grid md:grid-cols-2 gap-4" data-testid="lawyers-list">
        {items.length === 0 && <li className="panel p-10 text-center text-muted-foreground md:col-span-2">Sin resultados.</li>}
        {items.map((l) => (
          <li key={l.id} className="panel p-6 hover:border-foreground/40 transition-colors" data-testid={`lawyer-card-${l.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="serif text-2xl inline-flex items-center gap-2"><Scale size={18} className="text-amber-300"/> {l.name}</div>
                  {l.verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                  {l.is_founder && <span className="pill text-[10px] border-amber-400/40 bg-amber-400/10 text-amber-300">Fundador #{l.founder_number}</span>}
                </div>
                <div className="mt-1 text-sm text-muted-foreground inline-flex items-center gap-1.5"><MapPin size={13}/>{l.comuna}, {l.region}</div>
                {l.bio && <p className="mt-3 text-sm text-foreground/80 line-clamp-2">{l.bio}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {(l.specialties || []).map((s) => <span key={s} className="pill">{s}</span>)}
                </div>
              </div>
              <div className="text-right shrink-0">
                {l.reputation > 0 && (
                  <div className="inline-flex items-center gap-1 serif text-2xl">
                    <Star size={16} className="text-[hsl(var(--primary))]"/>{l.reputation}
                  </div>
                )}
                <div className="label-eyebrow mt-1">{l.reviews_count || 0} reseñas</div>
                {l.registry_number && <div className="text-xs text-muted-foreground mt-2">{l.registry_number}</div>}
              </div>
            </div>
            <div className="mt-5">
              <Link to={user ? `/app/abogados/${l.id}` : `/login?next=/app/abogados/${l.id}`} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid={`lawyer-view-${l.id}`}>
                Ver perfil <ArrowRight size={12}/>
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
